import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert, Quote, Lightbulb, Users, AlertTriangle, Target, Copy, Check } from "lucide-react";
import { cesteAggiorna, cesteStato, cesteLista, cesteUltimo, cesteReport } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

const Cit = ({ t }) => <p className="text-xs text-neutral-500 italic leading-snug">«{t}»</p>;

const CopyMini = ({ text, testid }) => {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={async () => { try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { toast.error("Copia non riuscita"); } }}
      data-testid={testid} title="Copia" className="shrink-0 p-1 rounded text-neutral-500 hover:text-white transition-colors">
      {ok ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
    </button>
  );
};

function Cesta({ icon: Icon, titolo, sotto, children, testid }) {
  return (
    <div className="rounded-lg border border-[#262626] bg-[#111] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1"><Icon size={14} className="text-[#D92D20]" /><h3 className="font-display font-bold text-white">{titolo}</h3></div>
      <p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 mb-4">{sotto}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Report({ r }) {
  return (
    <div className="space-y-4" data-testid="ceste-report">
      <div className="rounded-lg border border-[#D92D20]/40 bg-[#D92D20]/5 p-5" data-testid="ceste-una-cosa">
        <div className="flex items-center gap-2 mb-2"><Target size={14} className="text-[#D92D20]" /><p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-400">Una cosa sola da fare</p></div>
        <p className="text-base font-medium text-white leading-snug">{r.una_cosa}</p>
        {r.tono && <p className="mt-3 text-sm text-neutral-400 leading-relaxed"><span className="text-neutral-200 font-semibold">Il clima: </span>{r.tono}</p>}
      </div>

      {r.reel_da_segnalare?.length > 0 && (
        <div className="rounded-lg border border-amber-700/40 bg-amber-500/5 p-4" data-testid="ceste-segnalazioni">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={14} className="text-amber-400" /><p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-400">Reel da guardare</p></div>
          {r.reel_da_segnalare.map((x, i) => <p key={i} className="text-sm text-neutral-300"><span className="text-white font-semibold">{x.reel}</span> — {x.motivo}</p>)}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Cesta icon={ShieldAlert} titolo="Obiezioni ricorrenti" sotto="risposta pronta · concessione per i copioni" testid="cesta-obiezioni">
          {r.obiezioni?.map((o, i) => (
            <div key={i} className="border-l-2 border-[#333] pl-3">
              <p className="text-sm text-white font-semibold">{o.obiezione} {o.n ? <span className="text-neutral-500 font-normal">· ~{o.n}</span> : null}</p>
              {o.citazioni?.slice(0, 1).map((c, j) => <Cit key={j} t={c} />)}
              <div className="mt-1.5 flex items-start gap-1"><p className="text-sm text-neutral-200 leading-snug flex-1"><span className="text-neutral-500">Rispondi: </span>{o.risposta}</p><CopyMini text={o.risposta} testid={`cesta-obiezione-copy-${i}`} /></div>
              {o.concessione && <p className="mt-1 text-xs text-amber-300/90"><span className="text-neutral-500">Nei copioni: </span>{o.concessione}</p>}
            </div>
          ))}
        </Cesta>
        <Cesta icon={Quote} titolo="Frasi di casa" sotto="quello che si sentono dire · hook pronti" testid="cesta-frasi">
          {r.frasi_di_casa?.map((f, i) => (
            <div key={i} className="border-l-2 border-[#333] pl-3">
              <p className="text-sm text-white font-semibold">«{f.frase}»</p>
              {f.citazione && <Cit t={f.citazione} />}
              <div className="mt-1.5 flex items-start gap-1"><p className="font-display font-bold text-sm text-[#f5f5f5] leading-snug flex-1">{f.hook}</p><CopyMini text={f.hook} testid={`cesta-frase-copy-${i}`} /></div>
            </div>
          ))}
        </Cesta>
        <Cesta icon={Lightbulb} titolo="Temi che chiedono" sotto="cosa vogliono che tu spieghi" testid="cesta-temi">
          {r.temi_richiesti?.map((t, i) => (
            <div key={i} className="border-l-2 border-[#333] pl-3">
              <p className="text-sm text-white font-semibold">{t.tema}</p>
              <p className="text-xs text-neutral-400">{t.perche}</p>
              {t.citazioni?.slice(0, 1).map((c, j) => <Cit key={j} t={c} />)}
              <p className="mt-1.5 text-sm text-neutral-200 leading-snug"><span className="text-neutral-500">Reel: </span>{t.idea}</p>
            </div>
          ))}
        </Cesta>
        <Cesta icon={Users} titolo="Chi sono" sotto="dedotto da quello che dicono di sé" testid="cesta-chi">
          {r.chi_sono?.map((g, i) => (
            <div key={i} className="border-l-2 border-[#333] pl-3">
              <div className="flex items-center gap-2"><p className="text-sm text-white font-semibold">{g.gruppo}</p>{g.quota ? <span className="text-xs px-2 py-0.5 rounded-full border border-[#262626] text-neutral-300">{g.quota}%</span> : null}</div>
              {g.citazione && <Cit t={g.citazione} />}
              <p className="mt-1 text-sm text-neutral-200 leading-snug"><span className="text-neutral-500">Vuole: </span>{g.vuole}</p>
            </div>
          ))}
        </Cesta>
      </div>

      {r.reel?.length > 0 && (
        <p className="text-xs text-neutral-500" data-testid="ceste-reel-list">
          {r.n_commenti} commenti · {r.n_reel} reel · più commentati: {r.reel.slice(0, 3).map((x) => `«${x.titolo.slice(0, 40)}» (${x.n})`).join(" · ")}
        </p>
      )}
    </div>
  );
}

export default function Ceste() {
  const [report, setReport] = useState(null);
  const [meta, setMeta] = useState({ reports: [], last_sync: null, n_commenti_totali: 0 });
  const [stato, setStato] = useState({ state: "idle" });
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [u, m, s] = await Promise.all([cesteUltimo(), cesteLista(), cesteStato()]);
      setReport(u?.id ? u : null); setMeta(m); setStato(s);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel caricamento"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); return () => clearInterval(timer.current); }, [load]);

  const running = stato.state === "sync" || stato.state === "analisi";
  useEffect(() => {
    clearInterval(timer.current);
    if (!running) return;
    timer.current = setInterval(async () => {
      try {
        const s = await cesteStato(); setStato(s);
        if (s.state === "done") { clearInterval(timer.current); toast.success("Ceste aggiornate"); load(); }
        if (s.state === "error") { clearInterval(timer.current); toast.error(s.error || "Errore"); }
      } catch { /* riprovo al prossimo giro */ }
    }, 3000);
    return () => clearInterval(timer.current);
  }, [running, load]);

  const avvia = async () => {
    try { const r = await cesteAggiorna({ days, sync_days: 30 }); setStato(r.started ? { state: "sync" } : r.stato); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const apri = async (id) => { try { setReport(await cesteReport(id)); } catch { toast.error("Report non trovato"); } };

  return (
    <section className="mb-10" data-testid="ceste-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 mb-1">Le 4 ceste · commenti veri dai reel (Instagram API)</p>
          <h2 className="font-display font-extrabold text-2xl text-white">Cosa vi dicono, letto tutto</h2>
          <p className="mt-1 text-xs text-neutral-500" data-testid="ceste-meta">
            {meta.n_commenti_totali} commenti in archivio{meta.last_sync ? ` · ultima lettura ${fmt(meta.last_sync)}` : ""}{report ? ` · ultimo report ${fmt(report.created_at)} (ultimi ${report.days} giorni)` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="ceste-days" className="bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-white outline-none">
            <option value={7}>Ultimi 7 giorni</option><option value={14}>Ultimi 14 giorni</option><option value={30}>Ultimi 30 giorni</option>
          </select>
          <button onClick={avvia} disabled={running} data-testid="ceste-aggiorna-btn"
            className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#b8261b] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md transition-colors">
            {running ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {stato.state === "sync" ? "Leggo i commenti…" : stato.state === "analisi" ? "Riempio le ceste… (2-3 min)" : "Leggi e aggiorna"}
          </button>
        </div>
      </div>

      {meta.reports.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4 text-xs" data-testid="ceste-storico">
          {meta.reports.map((x) => (
            <button key={x.id} onClick={() => apri(x.id)} data-testid={`ceste-report-${x.id}`}
              className={`px-2.5 py-1 rounded-md border transition-colors ${report?.id === x.id ? "border-[#D92D20] text-white" : "border-[#262626] text-neutral-400 hover:text-white"}`}>
              {fmt(x.created_at)} · {x.days}gg · {x.n_commenti}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="flex items-center gap-3 text-neutral-400 text-sm py-6" data-testid="ceste-loading"><Loader2 size={16} className="animate-spin" /> Carico…</div>}
      {!loading && !report && (
        <div className="rounded-lg border border-dashed border-[#262626] p-10 text-center text-sm text-neutral-500" data-testid="ceste-empty">
          Nessun report ancora. Premi «Leggi e aggiorna»: scarico i commenti dei reel e li metto nelle 4 ceste.
        </div>
      )}
      {report && <Report r={report} />}
    </section>
  );
}
